import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchAdminAuditLogs,
  fetchAdminProperty,
  fetchAdminLandlords,
  fetchAdminUnits,
  normalizeUnit,
  updateAdminProperty,
  geocodeAdminProperty,
  fetchAdminPropertyDocuments,
  uploadAdminPropertyDocument,
  fetchAdminPropertyDocumentDownloadUrl,
  deleteAdminPropertyDocument,
} from "../../api/adminData";
import EntityActivityFeed from "./activity/EntityActivityFeed";
import EntityWorkReportsTab from "./tasks/EntityWorkReportsTab";
import { buildGoogleMapsSearchUrl } from "../../utils/googleMapsUrl";
import {
  formatAuditLog,
  auditActorDisplay,
  auditActionLabel,
  formatAuditTimestamp,
} from "../../utils/auditDisplay";
import { normalizeUnitTypeLabel } from "../../utils/unitDisplayId";

function auditDisplayCell(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function auditNarrativeOnlyChange(c) {
  const o = auditDisplayCell(c.old);
  return (c.label === "Ereignis" || c.label === "Details") && (o === "—" || o === "");
}

function unitTypeBadgeClasses(type) {
  const raw = String(type ?? "").trim();
  const normalized = normalizeUnitTypeLabel(raw);
  if (normalized === "Co-Living") {
    return "border border-[rgba(91,156,246,0.2)] bg-[rgba(91,156,246,0.1)] text-[#5b9cf6]";
  }
  if (raw === "Business Apartment") {
    return "border border-[rgba(157,124,244,0.2)] bg-[rgba(157,124,244,0.1)] text-[#9d7cf4]";
  }
  return "border border-[#1c2035] bg-[#191c28] text-[#8892b0]";
}

/**
 * The occupancy pill's copy when nothing canonical arrived — the repo's own
 * "<Ding> nicht verfügbar" wording, not a new phrase.
 */
const UNIT_OCCUPANCY_UNAVAILABLE = "Belegung nicht verfügbar";

/**
 * One tone for the occupancy pill, whatever it says.
 *
 * REPLACES `ownerUnitStatusBadgeClass`, WHICH IS GONE (OC1/D3). That helper was a
 * traffic light keyed on the German words of `Unit.occupancy_status`, a legacy
 * column no endpoint has written since Step 5.2 (`routes_admin_units.py:103,180`).
 * Its first branch painted an EMPTY value with the red "frei" styling, so every
 * unit created since then showed a red em dash.
 *
 * NEUTRAL, NOT A NEW TRAFFIC LIGHT: colouring "N / M belegt" needs a threshold
 * rule, and OC1 does not invent occupancy rules (its §4/§11).
 *
 * THEME TOKENS, NOT THIS PAGE'S HARDCODED HEX (Review/B2). The first version
 * reused the panel chrome `border-[#1c2035] bg-[#191c28] text-[#8892b0]` and
 * measured 2.61:1 in light — `index.css` repaints that family's TEXT (:342) but
 * has no rule for `.bg-[#191c28]`, leaving a dark pill on a light card. The
 * replacement is `DepositBadge`'s `neutral` tone verbatim
 * (`deposits/DepositBadge.jsx:62`): Vantio's existing semantic neutral, AA in
 * both themes per DEP-L1, and matched by none of the light-mode pill rules — so
 * nothing global needs adding. Kept identical to the twin in
 * `AdminOwnerDetailPage` on purpose; one pill, one tone.
 */
const UNIT_OCCUPANCY_PILL_CLASS = "border border-border bg-muted text-foreground";

/**
 * "N / M belegt" from the canonical current-day counters, or `null`.
 *
 * `*_snapshot` comes off `GET /admin/units`, which this page already loads
 * (`fetchAdminUnits`), enriched from `occupancy_service.get_unit_occupancy_batch`
 * (`unit_admin_service.py:511-521`) — the same owner `/admin/occupancy`, the unit
 * list and the portfolio map read. No new request (OC1 §12). Reserved is NOT
 * folded into occupied there and is not folded in here.
 *
 * `typeof`, not `Number(...)`: `Number(null)` is 0, so an absent counter would
 * print as an authoritative "nobody lives here" (same guard and reason as
 * `unitOccupancyPercent`, `rooms/roomDisplayHelpers.js:188-196`).
 *
 * A LOCAL TWIN OF THE ONE IN `AdminOwnerDetailPage`, on purpose: the two pages
 * had their own copies of the helper this replaces, and a third shared module for
 * eleven lines would widen a slice that is already at its file budget. Folding
 * the pair into one presentation helper belongs to OC4.
 */
function unitOccupancySummary(u) {
  const total = u?.total_rooms_snapshot;
  const occ = u?.occupied_rooms_snapshot;
  if (typeof total !== "number" || !Number.isFinite(total) || !(total > 0)) return null;
  if (typeof occ !== "number" || !Number.isFinite(occ)) return null;
  return `${occ} / ${total} belegt`;
}

function geocodingStatusPresentation(meta, snapshot, isCreate) {
  if (isCreate) {
    return {
      text: "Geocoding: Noch nicht berechnet",
      className: "text-[11px] text-[#4a5070]",
    };
  }
  if (meta) {
    if (meta.status === "ok") {
      return {
        text: "Geocoding: Erfolgreich",
        className: "text-[11px] font-medium text-[#3ddc84]",
      };
    }
    if (meta.status === "skipped") {
      if (meta.reason === "unchanged") {
        return {
          text: "Geocoding: Adresse unverändert",
          className: "text-[11px] text-[#4a5070]",
        };
      }
      if (meta.reason === "incomplete_address") {
        return {
          text: "Geocoding: Unvollständig",
          className: "text-[11px] font-medium text-[#f5a623]",
        };
      }
      if (meta.reason === "provider_unavailable") {
        return {
          text: "Geocoding: Nicht verfügbar",
          className: "text-[11px] font-medium text-[#f5a623]",
        };
      }
      return {
        text: "Geocoding: Übersprungen",
        className: "text-[11px] text-[#4a5070]",
      };
    }
    if (meta.status === "failed") {
      return {
        text: "Geocoding: Fehlgeschlagen",
        className: "text-[11px] font-medium text-[#ff5f6d]",
      };
    }
  }
  if (snapshot && snapshot.lat != null && snapshot.lng != null) {
    return {
      text: "Geocoding: Koordinaten vorhanden",
      className: "text-[11px] text-[#3ddc84]",
    };
  }
  return {
    text: "Geocoding: Noch nicht berechnet",
    className: "text-[11px] text-[#4a5070]",
  };
}

const inputClass =
  "w-full rounded-[8px] border border-[#1c2035] bg-[#141720] px-2.5 py-2 text-sm text-[#edf0f7] outline-none";
const inputReadOnlyClass = " cursor-default opacity-[0.88]";
const labelClass = "mb-1 block text-[10px] font-medium text-[#4a5070]";

const cardBase = "overflow-hidden rounded-[12px] border border-[#1c2035] bg-[#10121a]";
const cardHead = "flex flex-wrap items-center justify-between gap-2 border-b border-[#1c2035] px-[16px] py-[12px]";
const cardBody = "px-[16px] py-[14px]";
const cardTitle = "text-[11px] font-medium uppercase tracking-[0.5px] text-[#edf0f7]";

function formatPropertyDocumentDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function formatPropertyDocumentType(doc) {
  const mime = String(doc.mime_type || "").toLowerCase();
  const name = String(doc.file_name || "");
  const ext = name.includes(".") ? (name.split(".").pop() || "").toLowerCase() : "";

  if (mime.includes("pdf") || ext === "pdf") return "PDF";
  if (mime.includes("jpeg") || mime.includes("jpg") || ext === "jpg" || ext === "jpeg") return "JPG";
  if (mime.includes("png") || ext === "png") return "PNG";
  if (
    mime.includes("wordprocessingml") ||
    mime.includes("msword") ||
    ext === "docx" ||
    ext === "doc"
  ) {
    return "DOCX";
  }
  if (ext && /^[a-z0-9]+$/i.test(ext)) return ext.toUpperCase();
  return "Datei";
}

const PROPERTY_DOCUMENT_CATEGORY_LABELS = {
  rent_contract: "Mietvertrag",
  id_document: "Ausweis",
  debt_register: "Betreibungsregister",
  insurance: "Versicherung",
  other: "Sonstiges",
};

function formatPropertyDocumentCategoryLabel(category) {
  if (category == null || String(category).trim() === "") return "—";
  const k = String(category).trim();
  return PROPERTY_DOCUMENT_CATEGORY_LABELS[k] || k;
}

function propertyRowToForm(row) {
  if (!row) return null;
  return {
    landlord_id: row.landlord_id || "",
    title: row.title || "",
    street: row.street || "",
    house_number: row.house_number || "",
    zip_code: row.zip_code || "",
    city: row.city || "",
    country: row.country || "CH",
    status: row.status || "active",
    notes: row.notes || "",
  };
}

function AdminPropertyDetailPage() {
  const { propertyId } = useParams();
  const [landlords, setLandlords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const formBaselineRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastGeocodingMeta, setLastGeocodingMeta] = useState(null);
  const [coordinateSnapshot, setCoordinateSnapshot] = useState(null);
  const [geocodingRetrying, setGeocodingRetrying] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [propertyDocuments, setPropertyDocuments] = useState([]);
  const [propertyDocsLoading, setPropertyDocsLoading] = useState(true);
  const [propertyDocUploading, setPropertyDocUploading] = useState(false);
  const [propertyDocUploadError, setPropertyDocUploadError] = useState("");
  const [propertyDocCategory, setPropertyDocCategory] = useState("");
  const propertyDocFileInputRef = useRef(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState(null);
  const [form, setForm] = useState({
    landlord_id: "",
    title: "",
    street: "",
    house_number: "",
    zip_code: "",
    city: "",
    country: "CH",
    status: "active",
    notes: "",
  });

  useEffect(() => {
    if (!propertyId) return;
    setIsEditing(false);
    setLoading(true);
    setUnitsLoading(true);
    setError("");
    setDataReady(false);
    Promise.all([
      fetchAdminProperty(propertyId),
      fetchAdminLandlords(),
      fetchAdminUnits().catch(() => []),
    ])
      .then(([row, lords, unitItems]) => {
        const pid = String(propertyId);
        const items = Array.isArray(unitItems) ? unitItems : [];
        const matched = items
          .filter((u) => String(u.property_id ?? u.propertyId ?? "") === pid)
          .map((u) => normalizeUnit(u));
        setPropertyUnits(matched);

        if (!row) {
          setError("Liegenschaft nicht gefunden.");
          return;
        }
        setDataReady(true);
        setLandlords(lords || []);
        setLastGeocodingMeta(null);
        setCoordinateSnapshot(
          row.lat != null && row.lng != null ? { lat: row.lat, lng: row.lng } : { lat: null, lng: null }
        );
        const nextForm = propertyRowToForm(row);
        setForm(nextForm);
        formBaselineRef.current = nextForm ? { ...nextForm } : null;
      })
      .catch((e) => {
        setError(e.message || "Fehler beim Laden.");
        setDataReady(false);
        setPropertyUnits([]);
      })
      .finally(() => {
        setLoading(false);
        setUnitsLoading(false);
      });
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    setPropertyDocsLoading(true);
    fetchAdminPropertyDocuments(propertyId)
      .then((items) => setPropertyDocuments(Array.isArray(items) ? items : []))
      .catch(() => setPropertyDocuments([]))
      .finally(() => setPropertyDocsLoading(false));
  }, [propertyId]);

  const loadAuditLogs = useCallback(
    (opts = {}) => {
      const silent = opts.silent === true;
      if (!propertyId) return Promise.resolve();
      if (!silent) {
        setAuditLoading(true);
        setAuditError(null);
      }
      return fetchAdminAuditLogs({ entity_type: "property", entity_id: propertyId })
        .then((r) => {
          const items = Array.isArray(r?.items) ? r.items : [];
          setAuditLogs(items);
        })
        .catch(() => {
          if (!silent) {
            setAuditError("Verlauf konnte nicht geladen werden.");
            setAuditLogs([]);
          }
        })
        .finally(() => {
          if (!silent) setAuditLoading(false);
        });
    },
    [propertyId]
  );

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  function handlePropertyDocPick() {
    propertyDocFileInputRef.current?.click();
  }

  async function handlePropertyDocSelected(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !propertyId) return;
    setPropertyDocUploading(true);
    setPropertyDocUploadError("");
    try {
      await uploadAdminPropertyDocument(propertyId, f, {
        category: propertyDocCategory.trim() || undefined,
      });
      setPropertyDocCategory("");
      const items = await fetchAdminPropertyDocuments(propertyId);
      setPropertyDocuments(Array.isArray(items) ? items : []);
      await loadAuditLogs({ silent: true });
    } catch (err) {
      setPropertyDocUploadError(err.message || "Upload fehlgeschlagen.");
    } finally {
      setPropertyDocUploading(false);
    }
  }

  async function handleOpenPropertyDocument(docId) {
    try {
      const data = await fetchAdminPropertyDocumentDownloadUrl(docId);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(err.message || "Download konnte nicht gestartet werden.");
    }
  }

  async function handleDeletePropertyDocument(docId) {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await deleteAdminPropertyDocument(docId);
      const items = await fetchAdminPropertyDocuments(propertyId);
      setPropertyDocuments(Array.isArray(items) ? items : []);
      await loadAuditLogs({ silent: true });
    } catch (err) {
      window.alert(err.message || "Löschen fehlgeschlagen.");
    }
  }

  const handleCancelEdit = () => {
    const b = formBaselineRef.current;
    if (b) setForm({ ...b });
    setIsEditing(false);
    setError("");
  };

  const handleStartEdit = () => {
    formBaselineRef.current = { ...form };
    setIsEditing(true);
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEditing || !propertyId) return;
    setSaving(true);
    setError("");
    const body = {
      landlord_id: form.landlord_id.trim() || null,
      title: form.title.trim() || "—",
      street: form.street.trim() || null,
      house_number: form.house_number.trim() || null,
      zip_code: form.zip_code.trim() || null,
      city: form.city.trim() || null,
      country: form.country.trim() || "CH",
      status: form.status.trim() || "active",
      notes: form.notes.trim() || null,
    };
    updateAdminProperty(propertyId, body)
      .then((data) => {
        if (data) {
          setLastGeocodingMeta(data.geocoding ?? null);
          setCoordinateSnapshot(
            data.lat != null && data.lng != null ? { lat: data.lat, lng: data.lng } : { lat: null, lng: null }
          );
          const nextForm = propertyRowToForm(data);
          if (nextForm) {
            setForm(nextForm);
            formBaselineRef.current = { ...nextForm };
          }
        }
        const g = data && data.geocoding;
        if (g && g.status !== "ok" && g.reason && g.reason !== "unchanged") {
          const reason = g.reason;
          let msg =
            "Liegenschaft gespeichert, aber Koordinaten konnten nicht automatisch ermittelt werden.";
          if (reason === "incomplete_address") {
            msg =
              "Liegenschaft gespeichert, aber die Adresse war für die automatische Kartenposition unvollständig.";
          } else if (reason === "provider_unavailable") {
            msg =
              "Liegenschaft gespeichert, aber Koordinaten konnten nicht automatisch ermittelt werden (Geocoding nicht konfiguriert).";
          }
          toast.warning(msg);
        } else {
          toast.success("Liegenschaft gespeichert.");
        }
        setIsEditing(false);
        return loadAuditLogs({ silent: true });
      })
      .catch((e) => setError(e.message || "Speichern fehlgeschlagen."))
      .finally(() => setSaving(false));
  };

  const handleRetryGeocode = () => {
    if (!propertyId || geocodingRetrying) return;
    setGeocodingRetrying(true);
    geocodeAdminProperty(propertyId)
      .then((data) => {
        setLastGeocodingMeta(data.geocoding ?? null);
        setCoordinateSnapshot(
          data.lat != null && data.lng != null ? { lat: data.lat, lng: data.lng } : { lat: null, lng: null }
        );
        const g = data.geocoding;
        if (g && g.status === "ok") {
          toast.success("Koordinaten erfolgreich aktualisiert.");
        } else if (g && g.reason === "incomplete_address") {
          toast.warning("Adresse unvollständig – Koordinaten konnten nicht ermittelt werden.");
        } else if (g && g.reason === "provider_unavailable") {
          toast.warning("Geocoding nicht verfügbar.");
        } else {
          toast.warning("Koordinaten konnten nicht automatisch ermittelt werden.");
        }
        Promise.all([fetchAdminProperty(propertyId), fetchAdminLandlords()]).then(([row, lords]) => {
          if (row) {
            setLandlords(lords || []);
            setCoordinateSnapshot(
              row.lat != null && row.lng != null ? { lat: row.lat, lng: row.lng } : { lat: null, lng: null }
            );
          }
        });
      })
      .catch((e) => toast.error(e.message || "Geocoding fehlgeschlagen."))
      .finally(() => setGeocodingRetrying(false));
  };

  const geoPresent = geocodingStatusPresentation(lastGeocodingMeta, coordinateSnapshot, false);

  const landlordNameById = useMemo(() => {
    const m = {};
    for (const l of landlords) {
      if (l && l.id != null) {
        m[String(l.id)] = l.company_name || l.contact_name || l.email || String(l.id);
      }
    }
    return m;
  }, [landlords]);

  /** Verwaltung labels from assigned units only (Unit.landlord_id); not Property.landlord_id. */
  const verwaltungSummaryFromUnits = useMemo(() => {
    const labels = [];
    const seen = new Set();
    for (const u of propertyUnits) {
      const lid = u.landlord_id != null && String(u.landlord_id).trim() !== "" ? String(u.landlord_id) : "";
      if (!lid) continue;
      if (seen.has(lid)) continue;
      seen.add(lid);
      labels.push(landlordNameById[lid] || lid);
    }
    if (labels.length === 0) return "—";
    return labels.join(", ");
  }, [propertyUnits, landlordNameById]);

  /** Eigentümer labels from assigned units (normalizeUnit owner_id / ownerName). */
  const ownersSummaryFromUnits = useMemo(() => {
    const labels = [];
    const seen = new Set();
    for (const u of propertyUnits) {
      const oid = u.owner_id != null && String(u.owner_id).trim() !== "" ? String(u.owner_id) : "";
      const n = String(u.ownerName || "").trim();
      const key = oid || n;
      if (!key) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      labels.push(n || oid);
    }
    if (labels.length === 0) return "—";
    return labels.join(", ");
  }, [propertyUnits]);

  if (loading) {
    return (
      <p className="min-h-[40vh] bg-[#080a0f] px-6 py-8 text-[#4a5070]">Lade Liegenschaft …</p>
    );
  }

  if (error && !dataReady) {
    return (
      <div className="min-h-screen bg-[#080a0f] px-6 py-8 text-[#edf0f7]">
        <p className="mb-3 text-[14px] text-[#ff5f6d]">{error}</p>
        <Link
          to="/admin/properties"
          className="inline-block rounded-[6px] border border-[#252a3a] bg-[#141720] px-[12px] py-[4px] text-[11px] text-[#8892b0] no-underline hover:text-[#edf0f7]"
        >
          Zurück zur Liste
        </Link>
      </div>
    );
  }

  const displayTitle = String(form.title || "").trim() || "Liegenschaft";
  const hasAssignedUnits = propertyUnits.length > 0;
  const titleParts = displayTitle.split(/\s+/).filter(Boolean);
  let stammInitials = "?";
  if (titleParts.length >= 2) {
    const a = titleParts[0][0] || "";
    const b = titleParts[titleParts.length - 1][0] || "";
    stammInitials = `${a}${b}`.toUpperCase() || "?";
  } else if (displayTitle && displayTitle !== "Liegenschaft") {
    stammInitials = displayTitle.slice(0, 2).toUpperCase();
  } else {
    stammInitials = "L";
  }
  const line1Map = [form.street, form.house_number].filter(Boolean).join(" ").trim();
  const zipCity = [form.zip_code, form.city].filter(Boolean).join(" ").trim();
  const addrCombinedBlue = [line1Map, zipCity, form.country?.trim()].filter(Boolean).join(", ") || null;
  const canOpenMap = Boolean(line1Map || String(form.zip_code || "").trim() || String(form.city || "").trim());
  const roField = !isEditing;
  const inputFieldClass = `${inputClass}${roField ? inputReadOnlyClass : ""}`;

  return (
    <div className="-m-6 min-h-screen bg-[#080a0f]">
      <div className="sticky top-0 z-30 flex h-[50px] items-center justify-between border-b border-[#1c2035] bg-[#0c0e15] px-6 backdrop-blur-md">
        <div className="flex min-w-0 flex-1 items-center gap-[12px]">
          <Link
            to="/admin/properties"
            className="shrink-0 rounded-[6px] border border-[#252a3a] bg-[#141720] px-[10px] py-[4px] text-[11px] text-[#4a5070] no-underline hover:text-[#edf0f7]"
          >
            ← Liegenschaften
          </Link>
          <div className="h-[20px] w-px shrink-0 bg-[#1c2035]" aria-hidden />
          <div className="flex min-w-0 flex-wrap items-center gap-x-[8px] gap-y-1">
            <span className="shrink-0 text-[10px] text-[#4a5070]">Liegenschaft</span>
            <span className="shrink-0 text-[#1c2035]">/</span>
            <span className="truncate text-[14px] font-medium text-[#edf0f7]">{displayTitle}</span>
            {hasAssignedUnits ? (
              <span className="inline-flex shrink-0 items-center rounded-full border border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.1)] px-[8px] py-[1px] text-[10px] font-semibold text-[#3ddc84]">
                Aktiv
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center rounded-full border border-[#1c2035] bg-[#191c28] px-[8px] py-[1px] text-[10px] font-semibold text-[#4a5070]">
                Keine Zuweisung
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-[8px]">
          {!isEditing ? (
            <button
              type="button"
              onClick={handleStartEdit}
              className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-[12px] py-[4px] text-[11px] text-[#8892b0] hover:border-[#242840] hover:text-[#edf0f7]"
            >
              Bearbeiten
            </button>
          ) : null}
          <Link
            to="/admin/properties"
            className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-[12px] py-[4px] text-[11px] text-[#8892b0] no-underline hover:text-[#edf0f7]"
          >
            Zur Liste
          </Link>
        </div>
      </div>

      {dataReady ? (
        <div className="grid grid-cols-1 items-start gap-[16px] px-[24px] py-[20px] lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="flex min-w-0 flex-col gap-[12px]">
            {error ? <p className="text-[14px] text-[#ff5f6d]">{error}</p> : null}
            <div className="overflow-hidden rounded-[12px] border border-[#1c2035] bg-[#10121a]">
              <div className="flex items-center justify-between border-b border-[#1c2035] px-[16px] py-[12px]">
                <span className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#edf0f7]">Stammdaten</span>
              </div>
              <form onSubmit={handleSubmit} className="px-[16px] py-[14px]">
                <div className="mb-[14px] flex items-center gap-[12px]">
                  <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-[10px] border border-[rgba(91,156,246,0.2)] bg-[rgba(91,156,246,0.1)] text-[14px] font-semibold text-[#5b9cf6]">
                    {stammInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-[#edf0f7]">{displayTitle}</p>
                    <p className="mt-[2px] text-[10px] text-[#4a5070]">Liegenschaft · Property</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-3">
                  <div className="flex flex-col gap-[3px] sm:col-span-3">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Titel</span>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      readOnly={roField}
                      className={inputFieldClass}
                      required
                      placeholder="z.B. Haus Musterstrasse"
                    />
                  </div>
                  <div className="flex flex-col gap-[3px] sm:col-span-3">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Verwaltung</span>
                    <p className="m-0 text-[12px] font-medium text-[#edf0f7]">{verwaltungSummaryFromUnits}</p>
                  </div>
                  <div className="flex flex-col gap-[3px] sm:col-span-3">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Eigentümer</span>
                    <p className="m-0 text-[12px] font-medium text-[#edf0f7]">{ownersSummaryFromUnits}</p>
                  </div>
                </div>
                <div className="my-[12px] h-px bg-[#1c2035]" />
                <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-3">
                  <div className="col-span-full flex min-w-0 flex-wrap items-center gap-[8px]">
                    <span className="min-w-0 text-[11px] text-[#5b9cf6]">{addrCombinedBlue || "—"}</span>
                    {canOpenMap ? (
                      <button
                        type="button"
                        title="In Google Maps öffnen"
                        aria-label="In Google Maps öffnen"
                        onClick={() =>
                          window.open(
                            buildGoogleMapsSearchUrl(line1Map, form.zip_code, form.city),
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[6px] border border-[#252a3a] bg-[#141720] p-0.5 text-[#8892b0] transition-colors hover:border-[#3b5fcf] hover:text-[#edf0f7]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-[3px]">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Strasse</span>
                    <input
                      type="text"
                      value={form.street}
                      onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
                      readOnly={roField}
                      className={inputFieldClass}
                    />
                  </div>
                  <div className="flex flex-col gap-[3px]">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Hausnummer</span>
                    <input
                      type="text"
                      value={form.house_number}
                      onChange={(e) => setForm((f) => ({ ...f, house_number: e.target.value }))}
                      readOnly={roField}
                      className={inputFieldClass}
                    />
                  </div>
                  <div className="flex flex-col gap-[3px]">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">PLZ / Ort</span>
                    <div className="grid grid-cols-2 gap-[8px]">
                      <input
                        type="text"
                        value={form.zip_code}
                        onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))}
                        readOnly={roField}
                        className={inputFieldClass}
                        placeholder="PLZ"
                      />
                      <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                        readOnly={roField}
                        className={inputFieldClass}
                        placeholder="Ort"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-[3px] sm:col-span-3">
                    <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Land</span>
                    <input
                      type="text"
                      value={form.country}
                      onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                      readOnly={roField}
                      className={inputFieldClass}
                    />
                  </div>
                </div>
                <div className="my-[12px] h-px bg-[#1c2035]" />
                <p className={geoPresent.className}>{geoPresent.text}</p>
                <div className="mt-[10px]">
                  <button
                    type="button"
                    disabled={roField || saving || geocodingRetrying}
                    onClick={handleRetryGeocode}
                    className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-3 py-2 text-[12px] font-semibold text-[#8892b0] transition-colors hover:border-[#3b5fcf] hover:text-[#edf0f7] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {geocodingRetrying ? "Berechne …" : "Koordinaten erneut berechnen"}
                  </button>
                </div>
                <div className="mt-[12px] flex flex-col gap-[3px]">
                  <span className="text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Notizen</span>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    readOnly={roField}
                    className={inputFieldClass}
                    style={{ minHeight: "60px" }}
                    rows={2}
                  />
                </div>
                {isEditing ? (
                  <div className="mt-[16px] flex flex-wrap gap-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="rounded-[6px] border border-[rgba(91,156,246,0.28)] bg-[rgba(91,156,246,0.1)] px-[14px] py-[6px] text-[11px] font-medium text-[#5b9cf6] hover:bg-[rgba(91,156,246,0.15)] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {saving ? "Speichern …" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={handleCancelEdit}
                      className="rounded-[6px] border border-[#252a3a] bg-[#141720] px-[14px] py-[6px] text-[11px] font-medium text-[#8892b0] hover:border-[#242840] hover:text-[#edf0f7] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Abbrechen
                    </button>
                  </div>
                ) : null}
              </form>
            </div>

            {/* D4c3 — "Rapporte der Liegenschaft".
                The same EntityWorkReportsTab the five other entity pages
                mount, given the property scope. No second component and
                no client-side widening: the property_id filter alone
                already returns the work booked on the property AND on
                its units and rooms, because ME2a materialised those
                derived links on write and ME2b filters on the link
                table. A correlated EXISTS keeps it to one row and one
                sum per report however many links point here.

                Position: the wide main column, after the master data and
                before the archival blocks. Not the 280px sidebar — the
                section renders a seven-column table, which would scroll
                sideways there. Not below Dokumente either: "what has
                been done here" belongs next to what the property IS, not
                after its file drawer.

                The `dark` wrapper is the one styling concession. This
                page is pre-token legacy — 34 hard-coded hexes, no `dark:`
                variants, dark whatever the user's theme says — while the
                section is theme-aware. Without the wrapper it would
                render as a white card on a near-black page in light
                mode. Forcing the section's dark presentation is one
                class here; re-theming the page is its own sprint, and
                deliberately not this one. Deleting this wrapper is the
                last step of that sprint, not a loose end. */}
            <div className="dark">
              <EntityWorkReportsTab scope="property" entityId={propertyId} />
            </div>

            {/* D4d — "Operative Aktivität".
                Two histories on one page, and the split between them is
                the point. This shows what people DID: the tasks and
                reports linked to the property, including the ones
                reaching it through a room, a unit or a tenancy. The
                "Historie" card in the sidebar keeps what FIELDS BECAME —
                address edits, document upload and delete — with its
                old→new diffs.

                They must not overlap, which is why the backend gives
                this scope no audit-change source at all
                (``_ENTITY_SOURCES["property"]``, audit_stream=None).
                Without that, every property change would print twice on
                one screen in two different renderings — the one thing
                this slice exists to avoid. Nothing here reads
                /audit-logs; nothing in Historie reads the feed.

                The `dark` wrapper is D4c3's, for D4c3's reason: this
                page is pre-token legacy and dark whatever the theme
                says, while the feed is theme-aware. */}
            <div className="dark">
              <EntityActivityFeed
                entityType="property"
                entityId={propertyId}
                headingId="property-activity-heading"
                headingText="Operative Aktivität"
                description="Aufgaben und Rapporte zu dieser Liegenschaft · Stammdatenänderungen stehen in der Historie"
              />
            </div>

            <div className={cardBase}>
              <div className={`${cardHead} flex-wrap gap-2`}>
                <div className={cardTitle}>Dokumente</div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={propertyDocCategory}
                    onChange={(e) => setPropertyDocCategory(e.target.value)}
                    disabled={propertyDocUploading || !propertyId}
                    className="cursor-pointer appearance-none rounded-[6px] border border-[#1c2035] bg-[#141720] px-[8px] py-[4px] font-['DM_Sans'] text-[11px] text-[#8892b0] disabled:opacity-70"
                  >
                    <option value="">—</option>
                    <option value="rent_contract">Mietvertrag</option>
                    <option value="id_document">Ausweis</option>
                    <option value="debt_register">Betreibungsregister</option>
                    <option value="insurance">Versicherung</option>
                    <option value="other">Sonstiges</option>
                  </select>
                  <input
                    ref={propertyDocFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handlePropertyDocSelected}
                  />
                  <button
                    type="button"
                    onClick={handlePropertyDocPick}
                    disabled={propertyDocUploading || !propertyId}
                    className="rounded-[6px] border border-[rgba(91,156,246,0.28)] bg-[rgba(91,156,246,0.1)] px-[12px] py-[3px] text-[10px] font-medium text-[#5b9cf6] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {propertyDocUploading ? "Wird hochgeladen …" : "Hochladen"}
                  </button>
                </div>
              </div>
              <div className={cardBody}>
                {propertyDocUploadError ? (
                  <p className="mb-2 text-[13px] text-[#ff5f6d]">{propertyDocUploadError}</p>
                ) : null}
                {propertyDocsLoading ? (
                  <p className="m-0 text-[12px] text-[#4a5070]">Lade Dokumente …</p>
                ) : propertyDocuments.length === 0 ? (
                  <p className="m-0 text-[12px] text-[#4a5070]">Keine Dokumente vorhanden</p>
                ) : (
                  <div className="flex flex-col">
                    {propertyDocuments.map((doc) => (
                      <div
                        key={String(doc.id)}
                        className="flex items-center gap-[10px] border-b border-[#1c2035] py-[9px] last:border-b-0"
                      >
                        <div className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[6px] border border-[rgba(255,95,109,0.2)] bg-[rgba(255,95,109,0.1)] text-[9px] font-bold text-[#ff5f6d]">
                          {formatPropertyDocumentType(doc)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-medium text-[#edf0f7]">{doc.file_name || "—"}</div>
                          <div className="text-[10px] text-[#4a5070]">
                            {formatPropertyDocumentType(doc)} · {formatPropertyDocumentCategoryLabel(doc.category)} ·{" "}
                            {formatPropertyDocumentDate(doc.created_at)} ·{" "}
                            {doc.uploaded_by_name != null && doc.uploaded_by_name !== ""
                              ? doc.uploaded_by_name
                              : "—"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpenPropertyDocument(doc.id)}
                          className="shrink-0 rounded-[6px] border border-[#252a3a] bg-[#141720] px-[10px] py-[3px] text-[10px] text-[#8892b0] transition-colors hover:border-[#242840] hover:text-[#edf0f7]"
                        >
                          Öffnen →
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePropertyDocument(doc.id)}
                          className="shrink-0 rounded-[6px] border border-[#1c2035] bg-transparent px-[10px] py-[3px] text-[10px] text-[#4a5070] transition-colors hover:border-[rgba(255,95,109,0.2)] hover:bg-[rgba(255,95,109,0.1)] hover:text-[#ff5f6d]"
                        >
                          Löschen
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="flex min-w-0 flex-col gap-[12px]">
            <div className="overflow-hidden rounded-[12px] border border-[#1c2035] bg-[#10121a]">
              <div className="border-b border-[#1c2035] px-[16px] py-[12px]">
                <span className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#edf0f7]">Schnellinfo</span>
              </div>
              <div className="divide-y divide-[#1c2035]">
                <div className="flex items-center justify-between px-[16px] py-[9px]">
                  <span className="text-[11px] text-[#4a5070]">Status</span>
                  <span className="text-[12px] font-medium text-[#edf0f7]">
                    {hasAssignedUnits ? (
                      <span className="inline-flex items-center rounded-full border border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.1)] px-2 py-[1px] text-[10px] font-semibold text-[#3ddc84]">
                        Aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-[#1c2035] bg-[#191c28] px-2 py-[1px] text-[10px] font-semibold text-[#4a5070]">
                        Keine Zuweisung
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between px-[16px] py-[9px]">
                  <span className="text-[11px] text-[#4a5070]">Units zugeordnet</span>
                  <span className="text-[12px] font-medium text-[#edf0f7]">{propertyUnits.length}</span>
                </div>
                <div className="flex items-center justify-between gap-2 px-[16px] py-[9px]">
                  <span className="shrink-0 text-[11px] text-[#4a5070]">Verwaltung</span>
                  <span className="min-w-0 truncate text-right text-[12px] font-medium text-[#edf0f7]">
                    {verwaltungSummaryFromUnits}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 px-[16px] py-[9px]">
                  <span className="shrink-0 text-[11px] text-[#4a5070]">Eigentümer</span>
                  <span className="min-w-0 truncate text-right text-[12px] font-medium text-[#edf0f7]">
                    {ownersSummaryFromUnits}
                  </span>
                </div>
                <div className="px-[16px] py-[9px]">
                  <span className="block text-[11px] text-[#4a5070]">Geocoding</span>
                  <p className={`mt-[4px] mb-0 block ${geoPresent.className}`}>{geoPresent.text}</p>
                </div>
                <div className="flex items-center justify-between gap-2 px-[16px] py-[9px]">
                  <span className="shrink-0 text-[11px] text-[#4a5070]">Interne ID</span>
                  <span className="min-w-0 truncate font-mono text-[10px] text-[#8892b0]">{propertyId}</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[12px] border border-[#1c2035] bg-[#10121a]">
              <div className="flex items-center justify-between border-b border-[#1c2035] px-[16px] py-[12px]">
                <span className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#edf0f7]">
                  Zugeordnete Units
                </span>
                <span className="text-[10px] text-[#4a5070]">{propertyUnits.length}</span>
              </div>
              <div className="px-[16px] py-[14px]">
                {unitsLoading ? (
                  <div className="space-y-2" aria-busy="true">
                    <p className="text-[12px] text-[#4a5070]">Lade Units …</p>
                    <div className="h-2 w-full max-w-xs animate-pulse rounded bg-[#141720]" />
                    <div className="h-2 w-full max-w-[14rem] animate-pulse rounded bg-[#141720]" />
                  </div>
                ) : propertyUnits.length === 0 ? (
                  <p className="m-0 text-[11px] italic text-[#4a5070]">Keine Units zugeordnet</p>
                ) : (
                  <div className="max-h-[280px] overflow-y-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-[#242840] [&::-webkit-scrollbar-track]:bg-transparent">
                    <ul className="m-0 list-none p-0">
                      {propertyUnits.map((u, ui) => {
                        const uid = u.unitId ?? u.id;
                        const title = (u.title || u.name || "").trim() || "—";
                        const typeLabel = normalizeUnitTypeLabel(u.type) || String(u.type || "").trim() || "—";
                        const addr = String(u.address || "").trim();
                        const zip = String(u.zip ?? "").trim();
                        const city = String(u.city || "").trim();
                        const zipCity = [zip, city].filter(Boolean).join(" ");
                        const propTitle = String(u.property_title || "").trim();
                        const addrLine = [addr, zipCity].filter(Boolean).join(", ");
                        return (
                          <li key={String(uid)} className={ui < propertyUnits.length - 1 ? "mb-[8px]" : ""}>
                            <Link
                              to={`/admin/units/${encodeURIComponent(uid)}`}
                              className="block cursor-pointer rounded-[9px] border border-[#1c2035] bg-[#141720] px-[14px] py-[12px] transition-colors hover:border-[#242840]"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="min-w-0 truncate text-[12px] font-medium text-[#5b9cf6]">{title}</span>
                                <div className="flex shrink-0 gap-[4px]">
                                  <span
                                    className={`inline-flex items-center rounded-full px-[6px] py-[1px] text-[9px] font-semibold ${unitTypeBadgeClasses(u.type)}`}
                                  >
                                    {typeLabel}
                                  </span>
                                  {/* OCCUPANCY, FROM THE CANONICAL COUNTERS ONLY.
                                      `u.status` is the legacy
                                      `Unit.occupancy_status` column and is no
                                      longer read here (OC1/D3). The em dash keeps
                                      a reachable explanation: `title` is neither
                                      keyboard- nor touch-reachable on its own, so
                                      the same words also go in as screen-reader
                                      text. */}
                                  <span
                                    className={`inline-flex items-center rounded-full px-[6px] py-[1px] text-[9px] font-semibold ${UNIT_OCCUPANCY_PILL_CLASS}`}
                                    data-unit-occupancy
                                    title={
                                      unitOccupancySummary(u) === null
                                        ? UNIT_OCCUPANCY_UNAVAILABLE
                                        : undefined
                                    }
                                  >
                                    {unitOccupancySummary(u) ?? "—"}
                                    {unitOccupancySummary(u) === null ? (
                                      <span className="sr-only">
                                        {UNIT_OCCUPANCY_UNAVAILABLE}
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                              </div>
                              {addrLine ? (
                                <div className="mb-[6px] mt-[3px] text-[11px] text-[#4a5070]">
                                  {propTitle ? `${propTitle} · ` : ""}
                                  {addrLine}
                                </div>
                              ) : propTitle ? (
                                <div className="mb-[6px] mt-[3px] text-[11px] text-[#4a5070]">{propTitle}</div>
                              ) : (
                                <div className="mb-[6px] mt-[3px] font-mono text-[9px] text-[#4a5070]">{uid}</div>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-[12px] border border-[#1c2035] bg-[#10121a]">
              <div className="flex items-center justify-between border-b border-[#1c2035] px-[16px] py-[12px]">
                <span className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#edf0f7]">Historie</span>
                <span className="text-[10px] text-[#4a5070]">
                  {auditLoading ? "…" : `${auditLogs.length} Einträge`}
                </span>
              </div>
              <div className="px-[16px] py-[14px]">
                {auditLoading ? (
                  <p className="m-0 text-[12px] text-[#4a5070]">Lade Historie …</p>
                ) : auditError ? (
                  <p className="m-0 text-[12px] text-[#ff5f6d]">{auditError}</p>
                ) : auditLogs.length === 0 ? (
                  <p className="m-0 text-[12px] text-[#4a5070]">Keine Änderungen vorhanden</p>
                ) : (
                  <div className="max-h-[280px] overflow-y-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-[#242840] [&::-webkit-scrollbar-track]:bg-transparent">
                    <ul className="m-0 list-none p-0">
                      {auditLogs.map((log, li) => {
                        const { summary, changes } = formatAuditLog(log, {
                          entityType: "property",
                          landlordNameById,
                        });
                        const actorLine = auditActorDisplay(log);
                        return (
                          <li
                            key={log.id}
                            className={`py-[12px] ${li < auditLogs.length - 1 ? "border-b border-[#1c2035]" : ""}`}
                          >
                            <p className="mb-[4px] text-[11px] font-medium text-[#edf0f7]">{summary}</p>
                            <p className="mb-[8px] text-[10px] text-[#4a5070]">
                              <span>{formatAuditTimestamp(log.created_at)}</span>
                              {log.action != null && String(log.action) !== "" ? (
                                <>
                                  <span> · </span>
                                  <span>{auditActionLabel(log.action)}</span>
                                </>
                              ) : null}
                              {actorLine ? (
                                <>
                                  <span> · </span>
                                  <span className="text-[#f5a623]">{actorLine}</span>
                                </>
                              ) : null}
                            </p>
                            {changes && changes.length > 0
                              ? changes.map((c, ci) => {
                                  if (auditNarrativeOnlyChange(c)) {
                                    return (
                                      <div
                                        key={`${log.id}-${ci}`}
                                        className="mb-2 rounded-[6px] bg-[#141720] px-[10px] py-[6px] text-[12px] text-[#edf0f7]"
                                      >
                                        {auditDisplayCell(c.new)}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={`${log.id}-${ci}`} className="mb-2">
                                      <div className="mb-[4px] text-[10px] font-medium text-[#8892b0]">{c.label}</div>
                                      <div className="grid grid-cols-2 gap-[6px]">
                                        <div className="rounded-[6px] bg-[#141720] px-[10px] py-[6px]">
                                          <div className="mb-[3px] text-[8px] uppercase tracking-[0.5px] text-[#4a5070]">
                                            Alt
                                          </div>
                                          <div className="break-words font-mono text-[10px] text-[#ff5f6d]">
                                            {auditDisplayCell(c.old)}
                                          </div>
                                        </div>
                                        <div className="rounded-[6px] bg-[#141720] px-[10px] py-[6px]">
                                          <div className="mb-[3px] text-[8px] uppercase tracking-[0.5px] text-[#4a5070]">
                                            Neu
                                          </div>
                                          <div className="break-words font-mono text-[10px] text-[#3ddc84]">
                                            {auditDisplayCell(c.new)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

export default AdminPropertyDetailPage;
